import React from 'react';
import { Switch, Badge, Tooltip } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { WORKFLOW_TYPES, WORKFLOW_CONFIG, WORKFLOW_COLORS, UI_TEXT } from 'src/enums/EOutbound';

interface WorkflowItemProps {
	workflow: any;
	type: string;
	isSelected: boolean;
	onToggle: (workflowId: string, workflowType: string, enabled: boolean) => void;
	icon: React.ReactNode;
}

export const WorkflowItem: React.FC<WorkflowItemProps> = ({
	workflow,
	type,
	isSelected,
	onToggle,
	icon,
}) => {
	const config = WORKFLOW_CONFIG[type as keyof typeof WORKFLOW_CONFIG];
	const colors = WORKFLOW_COLORS[config.color as keyof typeof WORKFLOW_COLORS];

	const getWorkflowTitle = () => {
		if (type === WORKFLOW_TYPES.INTAKE) {
			return workflow.customScenario || 'Intake Form';
		}
		if (type === WORKFLOW_TYPES.SMS) {
			return workflow.description || 'SMS Workflow';
		}
		if (type === WORKFLOW_TYPES.TRANSFER) {
			return workflow.description || 'Call Transfer';
		}
		return 'Workflow';
	};

	const getWorkflowDescription = () => {
		if (type === WORKFLOW_TYPES.INTAKE) {
			const questionCount = workflow.questions?.length || 0;
			return `${questionCount} question${questionCount !== 1 ? 's' : ''}`;
		}
		if (type === WORKFLOW_TYPES.SMS) {
			return workflow.textMessage
				? workflow.textMessage.substring(0, 50) + (workflow.textMessage.length > 50 ? '...' : '')
				: 'Text message workflow';
		}
		if (type === WORKFLOW_TYPES.TRANSFER) {
			return workflow.workflowForwardingNumber
				? `Transfer to ${workflow.workflowForwardingNumber}`
				: 'Call transfer workflow';
		}
		return '';
	};

	return (
		<div
			className={`p-4 border-2 rounded-lg transition-all duration-200 cursor-pointer hover:shadow-md ${
				isSelected
					? `${colors.bg50} ${colors.border300} shadow-sm`
					: 'bg-white border-gray-200 hover:border-gray-300'
			}`}
			onClick={() => onToggle(workflow.id, type, !isSelected)}
		>
			<div className="flex items-start justify-between">
				<div className="flex items-start gap-3 flex-1">
					<div className={`p-2 rounded-lg ${isSelected ? colors.bg100 : 'bg-gray-100'}`}>
						<span className={isSelected ? colors.text600 : 'text-gray-600'}>{icon}</span>
					</div>
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-2 mb-1">
							<h4 className="font-medium text-gray-900 truncate">{getWorkflowTitle()}</h4>
							{isSelected && <CheckCircleOutlined className={`${colors.text600} text-sm`} />}
						</div>
						<p className="text-sm text-gray-600 break-words line-clamp-2">
							{getWorkflowDescription()}
						</p>

						{type === WORKFLOW_TYPES.INTAKE &&
							workflow.questions &&
							workflow.questions.length > 0 && (
								<div className="mt-2">
									<Tooltip
										title={
											<div className="max-w-xs">
												<div className="font-medium mb-1">Questions:</div>
												<ul className="list-disc list-inside space-y-1">
													{workflow.questions.slice(0, 3).map((q: string, idx: number) => (
														<li key={idx} className="text-xs">
															{q}
														</li>
													))}
													{workflow.questions.length > 3 && (
														<li className="text-xs">+{workflow.questions.length - 3} more...</li>
													)}
												</ul>
											</div>
										}
									>
										<Badge
											count={workflow.questions.length}
											className="mr-2"
											style={{ backgroundColor: '#52c41a' }}
										/>
										<span className="text-xs text-gray-500">{UI_TEXT.QUESTIONS}</span>
									</Tooltip>
								</div>
							)}
					</div>
				</div>

				<div onClick={(e) => e.stopPropagation()}>
					<Switch
						checked={isSelected}
						onChange={(checked) => onToggle(workflow.id, type, checked)}
						size="small"
						className="flex-shrink-0"
					/>
				</div>
			</div>
		</div>
	);
};
